import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import useMediaQuery from '@mui/material/useMediaQuery';
import { keyframes } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CloseIcon from '@mui/icons-material/Close';
import {
  QuestionAnswer,
  GitHub,
  Email,
  YouTube,
  Instagram
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { config } from '../Constants';
import { formatCurrency, getDisplayPrice } from '../utils/priceUtils';

const URL = config.url;
const spotlightSkus = [6614154, 6614153, 6614151];
const tcgSpotlightSkus = [6632394, 6632397, 6624826];
const desktopSpotlightTitlesBySku = {
  6614154: 'NVIDIA - GeForce RTX 5070 12GB GDDR7 Founders Edition',
  6614153: 'NVIDIA - GeForce RTX 5080 16GB GDDR7 Founders Edition',
  6614151: 'NVIDIA - GeForce RTX 5090 32GB GDDR7 Founders Edition',
  6632394: 'Pokemon - TCG: Scarlet & Violet White Flare',
  6632397: 'Pokemon - TCG: Scarlet & Violet Black Bolt',
  6624826: 'Pokemon - TCG: Scarlet & Violet Destined Rivals'
};
const mobileSpotlightTitlesBySku = {
  6614154: 'NVIDIA GeForce RTX 5070 Founders Edition',
  6614153: 'NVIDIA GeForce RTX 5080 Founders Edition',
  6614151: 'NVIDIA GeForce RTX 5090 Founders Edition',
  6632394: 'Pokemon Scarlet & Violet White Flare',
  6632397: 'Pokemon Scarlet & Violet Black Bolt',
  6624826: 'Pokemon Scarlet & Violet Destined Rivals'
};
const chromeExtensionUrl = 'https://chromewebstore.google.com/detail/stockmaid-best-buy-assist/eagpnnacaeonojmmkgnmnbgddfikfpfk';
const chromeExtensionVideoEmbedUrl = 'https://www.youtube-nocookie.com/embed/r8ccogwbXGI?autoplay=1&rel=0';
const statusPing = keyframes`
  0% { transform: scale(1); opacity: 0.45; }
  70% { transform: scale(2.1); opacity: 0; }
  100% { transform: scale(2.1); opacity: 0; }
`;

const DiscordIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
  </svg>
);

const TikTokIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74a2.89 2.89 0 0 1 2.31-4.64a2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const LandingPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktopSpotlight = useMediaQuery(theme.breakpoints.up('md'));
  // The hero phones are screenshots of a real alert, so they carry their own
  // light/dark chrome and have to follow the site theme rather than adapt to it.
  const heroPhoneSuffix = theme.palette.mode === 'dark' ? 'dark' : 'light';
  const heroNotifyImage = `/images/phone-notify-${heroPhoneSuffix}.webp`;
  const heroEmailImage = `/images/phone-email-${heroPhoneSuffix}.webp`;
  const [expandedFAQ, setExpandedFAQ] = useState(false);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [isLoadingFeaturedProducts, setIsLoadingFeaturedProducts] = useState(true);
  const [featuredTcgProducts, setFeaturedTcgProducts] = useState([]);
  const [isLoadingFeaturedTcgProducts, setIsLoadingFeaturedTcgProducts] = useState(true);
  const [activeFeaturedCollection, setActiveFeaturedCollection] = useState('gpu');
  const [isChromeExtensionVideoOpen, setIsChromeExtensionVideoOpen] = useState(false);
  const { isPremium, isAuthenticated, login } = useAuth();

  const handleFAQChange = (panel) => (event, isExpanded) => {
    setExpandedFAQ(isExpanded ? panel : false);
  };

  // Same three logos the product offer cards use, so the hero matches the rest of
  // the site. They have very different aspect ratios, which is why the products page
  // contains each one in a square box rather than sizing by height.
  const trackedRetailers = [
    { name: "Best Buy", logo: "/images/pics/Best_Buy_Logo.svg.png" },
    { name: "Micro Center", logo: "/images/pics/microcenter-logo.jpeg" },
    { name: "Newegg", logo: "/images/pics/newegg-logo.png" }
  ];

  const manufacturers = [
    {
      name: "ASUS",
      image: "/images/asus.webp"
    },
    {
      name: "GIGABYTE",
      image: "/images/gigabyte.webp"
    },
    {
      name: "MSI",
      image: "/images/msi.webp"
    },
    {
      name: "NVIDIA",
      image: "/images/nvidia.webp"
    },
    {
      name: "PNY",
      image: "/images/pny.webp"
    }
  ];

  const faqData = [
    {
      question: "What makes StockMaid different?",
      answer: "StockMaid offers a unique feature for premium users, allowing them to see the zip codes for Best Buy stores that are holding high-demand, low-supply products. This makes it easy to change your store, add the item to your cart, and check out before stock runs out."
    },
    {
      question: "How do I change from pickup to shipping?",
      answer: "Once your product has arrived at the Best Buy store for pickup, simply call the store with your order information and desired shipping address to request delivery instead."
    },
    {
      question: "Can I use it for free?",
      answer: "Yes, we provide free Discord notifications when a product is available in-store or online. Premium users, however, receive detailed email alerts and advanced tracking features."
    },
    {
      question: "Is it legal?",
      answer: "Yes, we only aggregate information that is publicly available on retailer websites. We do not bypass security measures, resell data, or automate purchases on behalf of users. We simply make it easier to track product availability in one place."
    }
  ];

  // Preload homepage images that are expensive to decode.
  useEffect(() => {
    ["/images/chrome-assistant-scene.webp", "/images/chrome-extensions-4312d461.png"].forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  useEffect(() => {
    if (isLoadingFeaturedProducts || isLoadingFeaturedTcgProducts) {
      return undefined;
    }

    const collections = {
      gpu: featuredProducts,
      tcg: featuredTcgProducts
    };
    const availableCollections = Object.keys(collections).filter(
      (key) => collections[key].length > 0
    );

    if (availableCollections.length <= 1) {
      if (!availableCollections.includes(activeFeaturedCollection) && availableCollections[0]) {
        setActiveFeaturedCollection(availableCollections[0]);
      }
      return undefined;
    }

    if (!availableCollections.includes(activeFeaturedCollection)) {
      setActiveFeaturedCollection(availableCollections[0]);
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setActiveFeaturedCollection((currentCollection) => {
        const currentIndex = availableCollections.indexOf(currentCollection);
        const nextIndex = currentIndex === -1
          ? 0
          : (currentIndex + 1) % availableCollections.length;
        return availableCollections[nextIndex];
      });
    }, 5500);

    return () => window.clearInterval(intervalId);
  }, [
    activeFeaturedCollection,
    featuredProducts,
    featuredTcgProducts,
    isLoadingFeaturedProducts,
    isLoadingFeaturedTcgProducts
  ]);

  useEffect(() => {
    let isMounted = true;

    const loadFeaturedProducts = async () => {
      setIsLoadingFeaturedProducts(true);
      setIsLoadingFeaturedTcgProducts(true);
      try {
        const fetchProducts = async (skus) => Promise.all(
          skus.map(async (sku) => {
            const response = await fetch(`${URL}/api/v1/stocks/sku/${sku}`);
            if (!response.ok) {
              throw new Error(`Failed loading SKU ${sku}`);
            }
            const data = await response.json();
            return data?.data || null;
          })
        );

        const [products, tcgProducts] = await Promise.all([
          fetchProducts(spotlightSkus),
          fetchProducts(tcgSpotlightSkus)
        ]);

        if (isMounted) {
          setFeaturedProducts(products.filter(Boolean));
          setFeaturedTcgProducts(tcgProducts.filter(Boolean));
        }
      } catch (error) {
        console.error('Failed to load featured products:', error);
        if (isMounted) {
          setFeaturedProducts([]);
          setFeaturedTcgProducts([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingFeaturedProducts(false);
          setIsLoadingFeaturedTcgProducts(false);
        }
      }
    };

    loadFeaturedProducts();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleTrackFromLanding = (sku) => {
    if (!isAuthenticated) {
      localStorage.setItem('redirectAfterLogin', `/skus/${sku}`);
      login(`/skus/${sku}`);
      return;
    }

    if (!isPremium) {
      navigate('/subscription');
      return;
    }

    navigate(`/skus/${sku}`);
  };

  const getSpotlightTitle = (stock) => {
    const titleMap = isDesktopSpotlight ? desktopSpotlightTitlesBySku : mobileSpotlightTitlesBySku;
    return titleMap[stock?.sku] || stock?.name || 'NVIDIA - GeForce RTX';
  };

  const handleOpenSpotlightProduct = (sku) => {
    navigate(`/skus/${sku}`);
  };

  const renderAvailabilityPill = (label, isAvailable) => (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1,
        py: 0.35,
        borderRadius: 999,
        bgcolor: isAvailable ? 'rgba(34,197,94,0.14)' : 'rgba(239,68,68,0.14)',
        color: isAvailable ? 'var(--sm-success-strong)' : 'var(--sm-danger-strong)'
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: isAvailable ? 'var(--sm-success-bright)' : 'var(--sm-danger)',
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            bgcolor: isAvailable ? 'var(--sm-success-bright)' : 'var(--sm-danger)',
            animation: `${statusPing} 1.8s ease-out infinite`
          }
        }}
      />
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, lineHeight: 1 }}>
        {label}
      </Typography>
    </Box>
  );

  const featuredCollections = {
    gpu: {
      label: 'Graphics Cards',
      products: featuredProducts
    },
    tcg: {
      label: 'Pokemon TCG',
      products: featuredTcgProducts
    }
  };
  const availableFeaturedCollections = Object.keys(featuredCollections).filter(
    (key) => featuredCollections[key].products.length > 0
  );
  const currentFeaturedCollection = availableFeaturedCollections.includes(activeFeaturedCollection)
    ? activeFeaturedCollection
    : availableFeaturedCollections[0] || 'gpu';
  const visibleFeaturedProducts = featuredCollections[currentFeaturedCollection]?.products || [];

  return (
    <>
      <Box sx={{ maxWidth: "1520px", margin: "0 auto", padding: { xs: "24px", md: "48px 24px" } }}>
        {/* Hero: phones + products side by side on desktop, stacked on mobile */}
        <Grid container spacing={4} alignItems="center" sx={{ mb: 8 }}>
          <Grid
            item
            xs={12}
            md={7}
            sx={{
              '@media (min-width:1200px)': {
                flexBasis: '55%',
                maxWidth: '55%'
              }
            }}
          >
            {/* Phones - desktop: side by side */}
            <Box sx={{
              display: { xs: 'none', md: 'flex' },
              justifyContent: 'center',
              alignItems: 'center',
              gap: { md: 4 },
              mt: { md: 0 }
            }}>
              <Box
                component="img"
                src={heroNotifyImage}
                alt="StockMaid alert on a phone lock screen"
                loading="eager"
                sx={{
                  width: { md: '280px' },
                  height: 'auto',
                  display: 'block',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.02)',
                  }
                }}
              />

              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--sm-heading-color)'
              }}>
                <ArrowForwardIcon sx={{ fontSize: { md: 48 } }} />
              </Box>

              <Box
                component="img"
                src={heroEmailImage}
                alt="StockMaid in-store pickup alert email"
                loading="eager"
                sx={{
                  width: { md: '280px' },
                  height: 'auto',
                  display: 'block',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.02)',
                  }
                }}
              />
            </Box>

            {/* Phones - mobile: overlapping */}
            <Box sx={{
              display: { xs: 'flex', md: 'none' },
              justifyContent: 'center',
              width: '100%',
              mt: { xs: 4, sm: 3 },
              mb: 2.5
            }}>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: { xs: 430, sm: 520 },
                  height: { xs: 460, sm: 540 }
                }}
              >
                <Box
                  component="img"
                  src={heroNotifyImage}
                  alt="StockMaid alert on a phone lock screen"
                  loading="eager"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    width: { xs: 222, sm: 262 },
                    height: 'auto',
                    transform: 'translateX(-96%)',
                    zIndex: 2,
                    filter: 'drop-shadow(0 10px 18px rgba(15, 23, 42, 0.14))'
                  }}
                />
                <Box
                  component="img"
                  src={heroEmailImage}
                  alt="StockMaid in-store pickup alert email"
                  loading="eager"
                  sx={{
                    position: 'absolute',
                    top: { xs: 6, sm: 12 },
                    left: '50%',
                    width: { xs: 242, sm: 286 },
                    height: 'auto',
                    transform: 'translateX(-9%)',
                    zIndex: 3,
                    filter: 'drop-shadow(0 10px 18px rgba(15, 23, 42, 0.14))'
                  }}
                />
              </Box>
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            md={5}
            sx={{
              '@media (min-width:1200px)': {
                flexBasis: '45%',
                maxWidth: '45%'
              }
            }}
          >
            <Typography
              variant="h2"
              component="h1"
              sx={{
                mb: 3,
                fontSize: { xs: '2rem', sm: '2.4rem', md: '2.6rem' },
                lineHeight: 1.2,
                color: 'var(--sm-heading-color)',
                fontWeight: 700
              }}
            >
              The ultimate product tracker.
            </Typography>

            <Typography
              variant="h6"
              component="p"
              color="text.primary"
              sx={{
                mb: 4,
                maxWidth: 640,
                fontWeight: 400,
                lineHeight: 1.75,
                fontSize: { xs: '1rem', md: '1.2rem' }
              }}
            >
              StockMaid has been tracking hundreds of high-demand products across{' '}
              {trackedRetailers.map((retailer, index) => (
                <Box key={retailer.name} component="span" sx={{ whiteSpace: 'nowrap' }}>
                  <Box
                    component="img"
                    src={retailer.logo}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    sx={{
                      width: { xs: 22, md: 28 },
                      height: { xs: 22, md: 28 },
                      objectFit: 'contain',
                      verticalAlign: '-0.4em',
                      mr: 0.6
                    }}
                  />
                  <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {retailer.name}
                  </Box>
                  {index < trackedRetailers.length - 2 ? ', ' : null}
                  {index === trackedRetailers.length - 2 ? ' and ' : null}
                </Box>
              ))}
              {' '}since{' '}
              <Box
                component="a"
                href="https://github.com/StockMaid/stock-app"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'inherit',
                  fontWeight: 600,
                  textDecoration: 'underline',
                  textUnderlineOffset: '2px',
                  '&:hover': { color: 'text.primary' }
                }}
              >
                2021
              </Box>
              .
            </Typography>

            <Box sx={{
              position: "relative",
              mx: "auto",
              px: { xs: 0, md: 0 }
            }}>
              {isLoadingFeaturedProducts || isLoadingFeaturedTcgProducts ? (
                <Typography
                  variant="body2"
                  align="center"
                  color="text.secondary"
                  sx={{ py: 2 }}
                >
                  Loading featured products...
                </Typography>
              ) : visibleFeaturedProducts.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                  <Button
                    variant="contained"
                    component={Link}
                    to="/stocks"
                    sx={{
                      bgcolor: 'var(--sm-btn-contained-bg)',
                      color: 'var(--sm-btn-contained-text)',
                      fontWeight: 700,
                      px: 3.5,
                      py: 1.2,
                      borderRadius: 2,
                      '&:hover': { bgcolor: 'var(--sm-btn-contained-hover)' }
                    }}
                  >
                    View all products
                  </Button>
                </Box>
              ) : (
                <>
                  <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
                    {availableFeaturedCollections.map((collectionKey) => {
                      const isActive = collectionKey === currentFeaturedCollection;

                      return (
                        <Chip
                          key={collectionKey}
                          label={featuredCollections[collectionKey].label}
                          onClick={() => setActiveFeaturedCollection(collectionKey)}
                          clickable
                          sx={{
                            borderRadius: 999,
                            bgcolor: isActive ? 'var(--sm-btn-contained-bg)' : 'var(--sm-soft-bg)',
                            color: isActive ? 'var(--sm-btn-contained-text)' : 'var(--sm-body-color)',
                            fontWeight: 800,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: isActive ? 'var(--sm-btn-contained-bg)' : 'var(--sm-soft-bg-hover)'
                            }
                          }}
                        />
                      );
                    })}
                  </Stack>
                  <Stack spacing={1}>
                    {visibleFeaturedProducts.map((stock) => (
                      <Card
                        key={stock.sku}
                        elevation={0}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleOpenSpotlightProduct(stock.sku)}
                        onKeyDown={(event) => {
                          if (event.target !== event.currentTarget) {
                            return;
                          }
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handleOpenSpotlightProduct(stock.sku);
                          }
                        }}
                        sx={{
                          borderRadius: '12px',
                          border: '1px solid var(--sm-border-strong)',
                          bgcolor: 'background.paper',
                          px: { xs: 0.7, sm: 1.4 },
                          py: { xs: 0.95, sm: 1.2 },
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: '48px minmax(0, 1fr) 64px',
                            sm: '52px minmax(0, 1fr) 68px',
                            md: '56px minmax(0, 1fr) 72px',
                            lg: '78px minmax(0, 1fr) 96px'
                          },
                          gridTemplateAreas: {
                            xs: '"thumb price cta" "thumb title cta" "thumb status cta"',
                            sm: '"thumb price cta" "thumb title cta" "thumb status cta"'
                          },
                          columnGap: { xs: 0.8, sm: 1.3 },
                          rowGap: { xs: 0.25, sm: 0.35 },
                          alignItems: 'center',
                          cursor: 'pointer',
                          position: 'relative',
                          transition: 'border-color 0.2s ease',
                          '&:hover': {
                            borderColor: 'var(--sm-heading-color)'
                          }
                        }}
                      >
                    <Box
                      component="img"
                      src={stock.image || stock.thumbnailImage || '/images/phone-track.webp'}
                      alt={stock.name}
                      loading="lazy"
                      sx={{
                        gridArea: 'thumb',
                        width: { xs: 48, sm: 52, md: 56, lg: 78 },
                        height: { xs: 48, sm: 52, md: 56, lg: 78 },
                        objectFit: 'contain',
                        bgcolor: 'var(--background-color-dark)',
                        borderRadius: 1,
                        border: '1px solid var(--sm-border-color)',
                        p: { xs: 0.2, sm: 0.35 }
                      }}
                    />

                    <Typography
                      variant="subtitle2"
                      sx={{
                        gridArea: 'price',
                        color: 'var(--sm-body-color)',
                        fontWeight: 800,
                        fontSize: { xs: '0.88rem', sm: '0.82rem', md: '0.85rem', lg: '0.94rem' },
                        lineHeight: 1.1
                      }}
                    >
                      {formatCurrency(getDisplayPrice(stock)) || 'Price unavailable'}
                    </Typography>

                    <Typography
                      sx={{
                        gridArea: 'title',
                        display: '-webkit-box',
                        color: 'var(--sm-heading-color)',
                        fontWeight: 700,
                        lineHeight: 1.1,
                        fontSize: { xs: '0.72rem', sm: '0.68rem', md: '0.71rem', lg: '0.88rem' },
                        letterSpacing: '-0.02em',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        minHeight: '1.1em'
                      }}
                    >
                      {getSpotlightTitle(stock)}
                    </Typography>

                    <Stack
                      direction="row"
                      spacing={0.7}
                      sx={{
                        gridArea: 'status',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        mt: 0.1
                      }}
                    >
                      {renderAvailabilityPill('In-store', !!stock.inStoreAvailability)}
                      {renderAvailabilityPill('Online', !!stock.onlineAvailability)}
                    </Stack>

                    <Button
                      variant="contained"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleTrackFromLanding(stock.sku);
                      }}
                      sx={{
                        gridArea: 'cta',
                        bgcolor: 'var(--sm-btn-contained-bg)',
                        color: 'var(--sm-btn-contained-text)',
                        fontWeight: 700,
                        borderRadius: 1.5,
                        minWidth: { xs: 64, sm: 68, md: 72, lg: 96 },
                        px: { xs: 0.55, sm: 0.45, md: 0.55, lg: 1.4 },
                        py: { xs: 0.62, sm: 0.74 },
                        fontSize: { xs: '0.72rem', sm: '0.68rem', md: '0.71rem', lg: '0.82rem' },
                        '&:hover': { bgcolor: 'var(--sm-btn-contained-hover)' }
                      }}
                    >
                      Track
                    </Button>

                  </Card>
                ))}
              </Stack>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                <Button
                  variant="contained"
                  component={Link}
                  to="/stocks"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    bgcolor: 'var(--sm-btn-contained-bg)',
                    color: 'var(--sm-btn-contained-text)',
                    fontWeight: 700,
                    borderRadius: 2,
                    px: 2.2,
                    py: 0.9,
                    width: { xs: '100%', sm: 'auto' },
                    '&:hover': {
                      bgcolor: 'var(--sm-btn-contained-hover)'
                    }
                  }}
                >
                  View all products
                </Button>
              </Box>
            </>
          )}
            </Box>
          </Grid>
        </Grid>

        <Box sx={{
          position: "relative",
          mt: { xs: 0, md: 4 },
          mb: { xs: 6, md: 8 },
          mx: "auto",
          px: 2
        }}>
          <Box
            sx={{
              position: "relative",
              borderRadius: 4,
              px: { xs: 3, md: 6 },
              py: { xs: 4, md: 6 },
              bgcolor: "#0f172a",
              color: "white",
              overflow: "hidden",
              boxShadow: "0 30px 80px rgba(15, 23, 42, 0.35)"
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background: "radial-gradient(circle at 20% 20%, rgba(88, 101, 242, 0.35), transparent 50%), radial-gradient(circle at 80% 0%, rgba(33, 182, 183, 0.25), transparent 45%)",
                pointerEvents: "none"
              }}
            />

            <Grid container spacing={4} alignItems="center" sx={{ position: "relative", zIndex: 1 }}>
              <Grid item xs={12} md={5}>
                <Chip
                  label="Free Discord Notifications"
                  sx={{
                    bgcolor: "rgba(88, 101, 242, 0.2)",
                    color: "white",
                    fontWeight: 600,
                    mb: 2
                  }}
                />
                <Typography
                  variant="h4"
                  component="h3"
                  fontWeight="bold"
                  sx={{ mb: 2, lineHeight: 1.2 }}
                >
                  Be first to know
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.85, mb: 3 }}>
                  Join our Discord to get lightning-fast alerts
                </Typography>
                <Stack spacing={1.5} sx={{ mb: 3 }}>
                  {[
                    "Real-time stock alerts in under a minute.",
                    "Alerts for RTX 5060, 5070, 5080 and 5090 GPUs.",
                    "Zero spam: only relevant notifications."
                  ].map((item) => (
                    <Box key={item} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "#5865F2"
                        }}
                      />
                      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
                <Box
                  sx={{
                    display: "inline-flex",
                    position: "relative",
                    zIndex: 2,
                    width: { xs: "100%", sm: "auto" },
                    "&::after": {
                      content: '""',
                      display: { xs: "block", sm: "none" },
                      position: "absolute",
                      left: 0,
                      right: 0,
                      top: "100%",
                      height: "clamp(56px, 14vw, 96px)",
                      background: "linear-gradient(180deg, rgba(15, 23, 42, 0.98) 0%, rgba(15, 23, 42, 0.7) 60%, rgba(15, 23, 42, 0) 100%)",
                      pointerEvents: "none"
                    }
                  }}
                >
                  <Button
                    variant="contained"
                    component="a"
                    href="https://discord.gg/W78pdPCdbC"
                    target="_blank"
                    rel="noreferrer"
                    size="large"
                    startIcon={<DiscordIcon />}
                    sx={{
                      bgcolor: "#5865F2",
                      color: "white",
                      px: 4,
                      py: 1.25,
                      fontWeight: "bold",
                      borderRadius: 2,
                      width: { xs: "100%", sm: "auto" },
                      "&:hover": { bgcolor: "#4752C4" }
                    }}
                  >
                    Join the Discord
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12} md={7}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mt: { xs: -3, sm: -4, md: -3 },
                    px: { xs: 0, sm: 0 }
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      maxWidth: { xs: 600, sm: 900, md: 1680 },
                      borderRadius: 0,
                      overflow: "hidden",
                      boxShadow: "none",
                      transform: {
                        xs: "translateY(-12px)",
                        sm: "translateY(-28px)",
                        md: "translateY(clamp(-44px, -4vw, -20px))"
                      }
                    }}
                  >
                    <Box
                      component="img"
                      src="/images/discord.webp"
                      alt="Discord notification preview for StockMaid"
                      loading="lazy"
                      sx={{
                        width: "100%",
                        display: "block",
                        objectFit: "cover"
                      }}
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>

        <Box
          sx={{
            mb: 8,
            px: { xs: 0, md: 0 },
            py: { xs: 1, md: 2 }
          }}
        >
          <Grid
            container
            spacing={{ xs: 4, md: 4 }}
            alignItems="center"
            direction={{ xs: 'column', md: 'row' }}
          >
            <Grid
              item
              xs={12}
              md={8}
              sx={{
                flexBasis: { md: '60%' },
                maxWidth: { md: '60%' }
              }}
            >
              <Box
                component="button"
                type="button"
                onClick={() => setIsChromeExtensionVideoOpen(true)}
                aria-label="Play StockMaid Chrome extension demo video"
                sx={{
                  position: 'relative',
                  display: 'block',
                  width: '100%',
                  maxWidth: { xs: 680, sm: 860, md: 980, lg: 1040 },
                  mx: 'auto',
                  pt: { xs: 0, md: 1 },
                  p: 0,
                  border: 0,
                  bgcolor: 'transparent',
                  cursor: 'pointer',
                  textAlign: 'left',
                  '&:hover .chrome-video-overlay': {
                    opacity: 1
                  },
                  '&:focus-visible': {
                    outline: '3px solid rgba(37, 99, 235, 0.35)',
                    outlineOffset: 6,
                    borderRadius: 6
                  }
                }}
              >
                <Box
                  component="img"
                  src="/images/chrome-assistant-scene.webp"
                  alt="MacBook mockup showing the StockMaid Chrome assistant flow"
                  loading="eager"
                  sx={{
                    display: 'block',
                    width: '100%',
                    height: 'auto',
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 28px 40px rgba(15, 23, 42, 0.12))'
                  }}
                />
                <Box
                  className="chrome-video-overlay"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: { xs: 1, md: 0.9 },
                    transition: 'opacity 0.2s ease'
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1.25,
                      px: { xs: 1.8, md: 2.1 },
                      py: { xs: 1.1, md: 1.2 },
                      borderRadius: 999,
                      bgcolor: 'rgba(15, 23, 42, 0.82)',
                      color: '#FFFFFF',
                      boxShadow: '0 16px 40px rgba(15, 23, 42, 0.22)',
                      backdropFilter: 'blur(6px)'
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 40, md: 44 },
                        height: { xs: 40, md: 44 },
                        borderRadius: '50%',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: "#FFFFFF",
                        color: "#0F172A",
                        flexShrink: 0
                      }}
                    >
                      <PlayArrowIcon sx={{ fontSize: 26 }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.95rem', md: '1rem' }, lineHeight: 1.1 }}>
                        Watch Demo
                      </Typography>
                      <Typography sx={{ fontSize: { xs: '0.76rem', md: '0.8rem' }, opacity: 0.82 }}>
                        See the extension in action
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              md={4}
              sx={{
                flexBasis: { md: '40%' },
                maxWidth: { md: '40%' }
              }}
            >
              <Box
                sx={{
                  maxWidth: { xs: '100%', md: 500 },
                  mx: 'auto'
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                  <Box
                    component="img"
                    src="/images/chrome-extensions-4312d461.png"
                    alt="StockMaid Chrome extension logo"
                    loading="lazy"
                    sx={{
                      width: { xs: 48, md: 56 },
                      height: { xs: 48, md: 56 },
                      borderRadius: 1.5
                    }}
                  />
                  <Typography
                    sx={{
                      color: 'var(--sm-heading-color)',
                      fontWeight: 800,
                      fontSize: { xs: '1rem', md: '1.05rem' },
                      letterSpacing: '-0.02em'
                    }}
                  >
                    StockMaid Chrome Extension
                  </Typography>
                </Stack>

                <Typography
                  variant="h4"
                  component="h2"
                  fontWeight="bold"
                  sx={{
                    mb: 2,
                    fontSize: { xs: '1.9rem', md: '2.25rem', lg: '2.5rem' },
                    lineHeight: 1.08,
                    color: 'var(--sm-heading-color)'
                  }}
                >
                  Automatic Add To Cart
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    mb: 2.25,
                    color: 'var(--sm-body-color)',
                    fontSize: { xs: '1rem', md: '1rem' },
                    lineHeight: 1.65,
                    maxWidth: 560
                  }}
                >
                  Automatically add your tracked products to the cart when they become available.
                </Typography>

                <Stack spacing={1} sx={{ mb: 2.5 }}>
                  {[
                    'Works for online shipping and in-store pickup',
                    'Rotates through your ZIP codes until successful',
                    'Checks tracked products every 30 seconds'
                  ].map((item) => (
                    <Box
                      key={item}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        color: 'var(--sm-body-color)'
                      }}
                    >
                      <Box
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          bgcolor: 'var(--sm-btn-contained-bg)',
                          flexShrink: 0
                        }}
                      />
                      <Typography sx={{ fontSize: { xs: '0.98rem', md: '0.96rem' }, lineHeight: 1.5 }}>
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 0.25, alignItems: { xs: 'stretch', sm: 'center' } }}>
                  <Button
                    variant="contained"
                    component="a"
                    href={chromeExtensionUrl}
                    target="_blank"
                    rel="noreferrer"
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      px: 4.5,
                      py: 1.3,
                      fontSize: '1rem',
                      borderRadius: 2.5,
                      bgcolor: 'var(--sm-btn-contained-bg)',
                      color: 'var(--sm-btn-contained-text)',
                      fontWeight: 700,
                      '&:hover': {
                        bgcolor: 'var(--sm-btn-contained-hover)'
                      }
                    }}
                  >
                    Get Chrome Extension
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setIsChromeExtensionVideoOpen(true)}
                    size="large"
                    startIcon={<PlayArrowIcon />}
                    sx={{
                      px: 3,
                      py: 1.3,
                      fontSize: '1rem',
                      borderRadius: 2.5,
                      borderColor: 'var(--sm-border-strong)',
                      color: 'var(--sm-heading-color)',
                      fontWeight: 700,
                      '&:hover': {
                        borderColor: 'var(--sm-muted-color)',
                        bgcolor: 'var(--sm-soft-bg)'
                      }
                    }}
                  >
                    Watch Demo
                  </Button>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Dialog
          open={isChromeExtensionVideoOpen}
          onClose={() => setIsChromeExtensionVideoOpen(false)}
          maxWidth="lg"
          fullWidth
          aria-labelledby="chrome-extension-demo-title"
          PaperProps={{
            sx: {
              bgcolor: '#020617',
              color: '#FFFFFF',
              borderRadius: { xs: 0, sm: 3 },
              overflow: 'hidden'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: { xs: 2, md: 3 }, py: 1.5 }}>
            <Typography id="chrome-extension-demo-title" sx={{ fontWeight: 800, fontSize: { xs: '1rem', md: '1.05rem' } }}>
              StockMaid Chrome Extension Demo
            </Typography>
            <IconButton onClick={() => setIsChromeExtensionVideoOpen(false)} sx={{ color: '#FFFFFF' }} aria-label="Close video dialog">
              <CloseIcon />
            </IconButton>
          </Box>
          <DialogContent sx={{ p: 0, bgcolor: '#000000' }}>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                pt: '56.25%'
              }}
            >
              {isChromeExtensionVideoOpen ? (
                <Box
                  component="iframe"
                  src={chromeExtensionVideoEmbedUrl}
                  title="StockMaid Chrome Extension Demo"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    border: 0
                  }}
                />
              ) : null}
            </Box>
          </DialogContent>
        </Dialog>

        <Box>
          <Typography variant="h4" component="h2" fontWeight="bold" color="text.primary" gutterBottom sx={{ mb: 4 }}>
            <QuestionAnswer sx={{ mr: 2, verticalAlign: "middle", color: "primary.main" }} />
            Frequently Asked Questions
          </Typography>

          {faqData.map((faq, index) => (
            <Accordion
              key={index}
              expanded={expandedFAQ === `panel${index}`}
              onChange={handleFAQChange(`panel${index}`)}
              disableGutters
              sx={{
                mb: 2,
                borderRadius: 2,
                '&:before': {
                  display: 'none',
                },
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                '&.Mui-expanded': {
                  margin: 0,
                  mb: 2,
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  '&:hover': {
                    bgcolor: 'rgba(88, 101, 242, 0.05)',
                  },
                  '& .MuiAccordionSummary-content': {
                    my: 2,
                  },
                }}
              >
                <Typography variant="h6" color="text.primary" sx={{ fontWeight: 500 }}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" color="text.primary">
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Box>

      <Box
        component="footer"
        sx={{
          mt: 8,
          py: 6,
          px: { xs: 2, md: 4 },
          bgcolor: 'var(--sm-navbar-bg)',
          color: 'white',
          boxShadow: '0 -4px 20px rgba(0,0,0,0.1)'
        }}
      >
        <Container maxWidth="xl">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <img
                  src="https://stockmaid.com/logo192.png"
                  alt="StockMaid Logo"
                  loading="lazy"
                  width="48"
                  height="48"
                  style={{ marginRight: 12 }}
                />
                <Typography variant="h5" fontWeight="bold">
                  StockMaid
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.8, mb: 2, fontSize: '0.875rem' }}>
                The ultimate product tracker.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <IconButton
                  component="a"
                  href="https://discord.gg/W78pdPCdbC"
                  target="_blank"
                  rel="noreferrer"
                  sx={{ color: 'white', bgcolor: '#5865F2', '&:hover': { bgcolor: '#4752C4' } }}
                >
                  <DiscordIcon />
                </IconButton>
                <IconButton
                  component="a"
                  href="https://www.tiktok.com/@stockmaid"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="StockMaid on TikTok"
                  sx={{ color: 'white', bgcolor: '#000', '&:hover': { bgcolor: '#25282d' } }}
                >
                  <TikTokIcon />
                </IconButton>
                <IconButton
                  component="a"
                  href="https://www.youtube.com/@StockMaid"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="StockMaid on YouTube"
                  sx={{ color: 'white', bgcolor: '#FF0000', '&:hover': { bgcolor: '#CC0000' } }}
                >
                  <YouTube />
                </IconButton>
                <IconButton
                  component="a"
                  href="https://www.instagram.com/stock_maid/"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="StockMaid on Instagram"
                  sx={{ color: 'white', background: 'linear-gradient(45deg, #F58529, #DD2A7B 50%, #8134AF)', '&:hover': { opacity: 0.9 } }}
                >
                  <Instagram />
                </IconButton>
                <IconButton
                  component="a"
                  href="https://github.com/StockMaid/stock-app"
                  target="_blank"
                  rel="noreferrer"
                  sx={{ color: 'white', bgcolor: '#333', '&:hover': { bgcolor: '#24292e' } }}
                >
                  <GitHub />
                </IconButton>
                <IconButton
                  component="a"
                  href="mailto:support@stockmaid.com"
                  sx={{ color: 'white', bgcolor: '#EA4335', '&:hover': { bgcolor: '#D33B2C' } }}
                >
                  <Email />
                </IconButton>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Product
              </Typography>
              <Stack spacing={1}>
                <Typography
                  component={Link}
                  to="/stocks"
                  variant="body2"
                  sx={{ color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}
                >
                  Products
                </Typography>
                <Typography
                  component={Link}
                  to="/subscription"
                  variant="body2"
                  sx={{ color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}
                >
                  Premium
                </Typography>
                <Typography
                  component={Link}
                  to="/my-tracked-stocks"
                  variant="body2"
                  sx={{ color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}
                >
                  Tracked Products
                </Typography>
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Manufacturers
              </Typography>
              <Stack spacing={1}>
                {manufacturers.map((manufacturer) => (
                  <Typography
                    key={manufacturer.name}
                    component={Link}
                    to={`/manufacturers/${manufacturer.name}`}
                    variant="body2"
                    sx={{ color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}
                  >
                    {manufacturer.name}
                  </Typography>
                ))}
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Support
              </Typography>
              <Stack spacing={1}>
                <Typography
                  component="a"
                  href="https://discord.gg/W78pdPCdbC"
                  target="_blank"
                  rel="noreferrer"
                  variant="body2"
                  sx={{ color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}
                >
                  Discord Community
                </Typography>
                <Typography
                  component="a"
                  href="mailto:support@stockmaid.com"
                  variant="body2"
                  sx={{ color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}
                >
                  Contact Us
                </Typography>
                <Typography
                  component={Link}
                  to="/profile"
                  variant="body2"
                  sx={{ color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}
                >
                  My Account
                </Typography>
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Legal
              </Typography>
              <Stack spacing={1}>
                <Typography
                  component="a"
                  href="/privacy"
                  variant="body2"
                  sx={{ color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}
                >
                  Privacy Policy
                </Typography>
                <Typography
                  component="a"
                  href="/terms"
                  variant="body2"
                  sx={{ color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}
                >
                  Terms of Service
                </Typography>
                <Typography
                  component="a"
                  href="/refund"
                  variant="body2"
                  sx={{ color: 'white', opacity: 0.8, textDecoration: 'none', '&:hover': { opacity: 1 } }}
                >
                  Refund Policy
                </Typography>
              </Stack>
            </Grid>
          </Grid>

          <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.1)' }} />

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ opacity: 0.6 }}>
              © {new Date().getFullYear()} StockMaid. All rights reserved.
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.4, mt: 1, display: 'block' }}>
              StockMaid is not affiliated with Best Buy, NVIDIA, Pokemon, or any manufacturer. All product names, logos, and brands are property of their respective owners.
            </Typography>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default LandingPage;
